import React, { useContext, useState } from 'react';
import { motion } from 'framer-motion';
import LanguageContext from '../contexts/LanguageContext';
import { getTranslation } from '../services/languageService';
import './Contact.scss';

const Contact = () => {
  const { language } = useContext(LanguageContext);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5
      }
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError('');
    
    // Simulate form submission
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      setSubmitSuccess(true);
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: ''
      });
      
      // Reset success message after 5 seconds
      setTimeout(() => {
        setSubmitSuccess(false);
      }, 5000);
    } catch (error) {
      setSubmitError(getTranslation('common.error', language));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <motion.div
      className="contact"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <section className="section">
        <div className="container">
          <motion.h1 className="page-title text-center" variants={itemVariants}>
            {getTranslation('contact.title', language)}
          </motion.h1>
          
          <div className="contact-content">
            <motion.div className="contact-form-section" variants={itemVariants}>
              <h2 className="section-title">{getTranslation('contact.title', language)}</h2>
              
              {submitSuccess && (
                <div className="alert alert-success">
                  {getTranslation('contact.form.send', language)} successfully!
                </div>
              )}
              
              {submitError && (
                <div className="alert alert-error">
                  {submitError}
                </div>
              )}
              
              <form className="contact-form" onSubmit={handleSubmit}>
                <div className="form-group">
                  <label htmlFor="name">{getTranslation('contact.form.name', language)} *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="email">{getTranslation('contact.form.email', language)} *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="subject">{getTranslation('contact.form.subject', language)} *</label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="message">{getTranslation('contact.form.message', language)} *</label>
                  <textarea
                    id="message"
                    name="message"
                    rows="5"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                  ></textarea>
                </div>
                
                <button 
                  type="submit" 
                  className="btn"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? getTranslation('common.loading', language) : getTranslation('contact.form.send', language)}
                </button>
              </form>
            </motion.div>
            
            <motion.div className="contact-info-section" variants={itemVariants}>
              <h2 className="section-title">{getTranslation('footer.contactInfo', language)}</h2>
              
              <div className="contact-info">
                <div className="info-item">
                  <h3>{getTranslation('contact.info.address', language)}</h3>
                  <p>No.161, Jalan Tunku Putra, 09000 Kulim, Kedah, Malaysia</p>
                </div>
                
                <div className="info-item">
                  <h3>{getTranslation('contact.info.phone', language)}</h3>
                  <p>+60 124823757</p>
                </div>
                
                <div className="info-item">
                  <h3>{getTranslation('contact.info.email', language)}</h3>
                  <p>appliedintelli.my@gmail.com</p>
                </div>
                
                <div className="info-item">
                  <h3>Business Hours</h3>
                  <p>Monday - Friday: 9:00 AM - 5:00 PM</p>
                  <p>Saturday & Sunday: Closed</p>
                </div>
                
                <div className="info-item">
                  <h3>SME Support Hotline</h3>
                  <p>For small and medium enterprises, we offer priority support during business hours.</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </motion.div>
  );
};

export default Contact;
